import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowUpRight, CalendarDays, Clock, MessageCircle, Share2, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppFloat from '../components/WhatsAppFloat';
import { WHATSAPP_LINK } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Article() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/posts/${slug}`)
      .then((res) => { if (active) setPost(res.data); })
      .catch(() => { if (active) setPost(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; window.speechSynthesis?.cancel(); };
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total <= 0 ? 0 : Math.min(1, window.scrollY / total));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const articleText = useMemo(() => {
    if (!post) return '';
    return [post.title, post.introduction, ...(post.sections || []).flatMap((s) => [s.heading, s.content]), post.conclusion].join('. ');
  }, [post]);

  useEffect(() => {
    if (!post) return;
    const title = post.seo_metadata?.meta_title || `${post.title} | Thesis`;
    const description = post.seo_metadata?.meta_description || post.excerpt;
    const canonical = post.seo_metadata?.canonical_url || window.location.href;
    document.title = title.includes('Thesis') ? title : `${title} | Thesis`;
    upsertMeta('description', description);
    upsertMeta('robots', 'index,follow');
    upsertMetaProperty('og:title', post.title);
    upsertMetaProperty('og:description', description);
    upsertMetaProperty('og:type', 'article');
    upsertMetaProperty('og:url', canonical);
    upsertMetaProperty('og:image', post.cover_image);
    upsertCanonical(canonical);
    upsertJsonLd('article-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description,
      image: post.cover_image,
      datePublished: post.created_at,
      dateModified: post.updated_at || post.created_at,
      author: { '@type': 'Organization', name: 'Thesis' },
      publisher: { '@type': 'Organization', name: 'Thesis' },
      mainEntityOfPage: canonical,
    });
  }, [post]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: post.title, text: post.excerpt, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copiado.');
      }
    } catch (err) {
      toast.error('Não consegui compartilhar agora.');
    }
  };

  const handleListen = () => {
    if (!('speechSynthesis' in window)) {
      toast.error('Seu navegador não suporta leitura em voz alta.');
      return;
    }
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const utterance = new SpeechSynthesisUtterance(articleText);
    utterance.lang = 'pt-BR';
    utterance.rate = 0.95;
    utterance.onend = () => setSpeaking(false);
    window.speechSynthesis.speak(utterance);
    setSpeaking(true);
  };

  return (
    <main className="relative min-h-screen bg-[#0A0B0E] text-[#F5F1E8]">
      <div data-testid="reading-progress-bar" className="fixed left-0 top-0 z-[120] h-[2px] bg-[#D4AF37] transition-transform duration-100 origin-left" style={{ transform: `scaleX(${progress})`, width: '100%' }} />
      <Header />

      {loading && <ArticleLoading />}
      {!loading && !post && <ArticleNotFound />}
      {!loading && post && (
        <>
          <article data-testid="article-page-container">
            <section className="relative min-h-[72vh] flex flex-col justify-end overflow-hidden grain">
              <img src={post.cover_image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-[#0A0B0E]/78 to-[#0A0B0E]/20" />
              <div className="thesis-container relative z-10 pt-36 pb-12 md:pb-20">
                <a href="/blog" data-testid="article-back-link" className="cursor-hover inline-flex items-center gap-2 text-[12px] font-mono-label text-[#D4AF37] hover:text-[#F5F5F5] transition-colors">
                  <ArrowLeft size={14} /> Voltar ao blog
                </a>
                <div className="mt-8 max-w-[1050px]">
                  <div className="flex flex-wrap items-center gap-4 text-[11px] font-mono-label text-[#A3A3A3]">
                    <span data-testid="article-publish-date" className="inline-flex items-center gap-2"><CalendarDays size={13} /> {new Date(post.created_at).toLocaleDateString('pt-BR')}</span>
                    <span data-testid="article-reading-time" className="inline-flex items-center gap-2"><Clock size={13} /> {post.read_time_minutes} min</span>
                  </div>
                  <h1 data-testid="article-title" className="mt-6 font-editorial text-[50px] sm:text-[78px] lg:text-[112px] leading-[0.86] tracking-normal text-[#F5F5F5]">
                    {post.title}
                  </h1>
                  <p data-testid="article-excerpt" className="mt-7 max-w-3xl text-[18px] md:text-[22px] leading-relaxed text-[#A3A3A3]">{post.excerpt}</p>
                </div>
              </div>
            </section>

            <section className="thesis-container relative py-14 md:py-24">
              <div className="mx-auto grid max-w-[1180px] grid-cols-1 lg:grid-cols-[190px_minmax(0,760px)_190px] gap-8">
                <aside className="hidden lg:block">
                  <div data-testid="article-table-of-contents" className="sticky top-28 text-[11px] font-mono-label text-[#737373] space-y-3">
                    <span className="block text-[#D4AF37]">Neste artigo</span>
                    {(post.sections || []).slice(0, 6).map((section, index) => (
                      <a key={`${section.heading}-${index}`} href={`#secao-${index + 1}`} data-testid={`article-toc-link-${index}`} className="block leading-relaxed text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors">
                        {String(index + 1).padStart(2, '0')} · {section.heading}
                      </a>
                    ))}
                  </div>
                </aside>

                <div data-testid="article-content" className="article-prose">
                  <p className="article-intro">{post.introduction}</p>
                  {(post.sections || []).map((section, index) => (
                    <section key={`${section.heading}-${index}`} id={`secao-${index + 1}`} data-testid={`article-section-${index}`} className="scroll-mt-28">
                      <h2>{section.heading}</h2>
                      {splitParagraphs(section.content).map((paragraph, pIndex) => <p key={pIndex}>{paragraph}</p>)}
                      {index === 1 && <PullQuote text="SEO bom não parece um truque. Parece clareza no momento em que o cliente procura uma resposta." />}
                    </section>
                  ))}
                  <section data-testid="article-conclusion-section">
                    <h2>Próximo passo</h2>
                    {splitParagraphs(post.conclusion).map((paragraph, pIndex) => <p key={pIndex}>{paragraph}</p>)}
                  </section>
                  <ArticleCTA title={post.title} />
                  {post.research_sources?.length > 0 && (
                    <section data-testid="article-sources-section" className="article-sources">
                      <h2>Fontes consultadas</h2>
                      <ul>
                        {post.research_sources.slice(0, 5).map((source, index) => (
                          <li key={`${source.url}-${index}`}>
                            <a href={source.url} target="_blank" rel="noopener noreferrer" data-testid={`article-source-link-${index}`}>{source.title}</a>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </div>

                <aside className="hidden lg:block">
                  <FloatingActions speaking={speaking} onListen={handleListen} onShare={handleShare} />
                </aside>
              </div>
            </section>
          </article>
          <div className="lg:hidden fixed bottom-5 left-5 right-5 z-50 flex justify-center gap-3 pointer-events-none">
            <FloatingActions speaking={speaking} onListen={handleListen} onShare={handleShare} compact />
          </div>
        </>
      )}

      <Footer />
      <WhatsAppFloat />
    </main>
  );
}

function splitParagraphs(text) {
  return String(text || '').split(/\n+/).map((p) => p.trim()).filter(Boolean);
}

function upsertMeta(name, content) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content || '');
}

function upsertMetaProperty(property, content) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content || '');
}

function upsertCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function PullQuote({ text }) {
  return <blockquote data-testid="article-pull-quote">“{text}”</blockquote>;
}

function ArticleCTA({ title }) {
  const message = `Olá! Li o artigo "${title}" no blog da Thesis e quero entender como melhorar meu site/marketing.`;
  return (
    <aside data-testid="article-conversion-cta" className="my-14 rounded-[8px] border border-[#D4AF37]/25 bg-[#D4AF37]/10 p-6 md:p-8">
      <span className="font-mono-label text-[#D4AF37]">CONVERSA ESTRATÉGICA</span>
      <h2 className="!mt-4 !mb-4">Quer aplicar isso no seu negócio?</h2>
      <p className="text-[#D7D7D7]">
        A Thesis transforma estratégia, site e conteúdo em uma presença digital mais clara para empresas de Belo Horizonte.
      </p>
      <a href={WHATSAPP_LINK(message)} target="_blank" rel="noopener noreferrer" data-testid="article-whatsapp-cta-link" className="mt-6 inline-flex items-center gap-3 rounded-full bg-[#D4AF37] px-5 py-3 text-[14px] font-semibold text-[#0A0B0E] transition-colors hover:bg-[#F5F5F5]">
        <MessageCircle size={17} /> Falar com a Thesis <ArrowUpRight size={16} />
      </a>
    </aside>
  );
}

function FloatingActions({ speaking, onListen, onShare, compact = false }) {
  return (
    <div className={`${compact ? 'flex gap-3 pointer-events-auto' : 'sticky top-28 flex flex-col gap-3'}`}>
      <button onClick={onListen} data-testid="listen-article-button" className="cursor-hover inline-flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-4 py-3 text-[12px] font-semibold text-[#D4AF37] backdrop-blur-xl transition-colors hover:bg-[#D4AF37] hover:text-[#0A0B0E]">
        {speaking ? <VolumeX size={15} /> : <Volume2 size={15} />} {compact ? '' : speaking ? 'Parar' : 'Ouvir'}
      </button>
      <button onClick={onShare} data-testid="share-article-button" className="cursor-hover inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-[#0A0B0E]/80 px-4 py-3 text-[12px] font-semibold text-[#F5F5F5] backdrop-blur-xl transition-colors hover:border-white/35">
        <Share2 size={15} /> {compact ? '' : 'Compartilhar'}
      </button>
    </div>
  );
}

function ArticleLoading() {
  return <div data-testid="article-loading-state" className="thesis-container min-h-screen pt-40"><div className="h-[60vh] rounded-[8px] glass animate-pulse" /></div>;
}

function ArticleNotFound() {
  return (
    <section data-testid="article-not-found-state" className="thesis-container min-h-screen pt-40 pb-20">
      <a href="/blog" data-testid="article-not-found-back-link" className="text-[#D4AF37] font-mono-label">← VOLTAR AO BLOG</a>
      <h1 className="mt-8 font-editorial text-[58px] md:text-[96px] leading-none text-[#F5F5F5]">Artigo não encontrado.</h1>
    </section>
  );
}